import { toStr } from "../fn/toStr";

export const LEFT = Symbol("__left__");
export const RIGHT = Symbol("__right__");

const throwTypeError = msg => {
	throw new TypeError(msg);
};

export const Left = error => ({ tag: LEFT, error });

export const leftFrom = fn => (...args) => Left(fn(...args));
Left.from = leftFrom;

export const Right = result => ({ tag: RIGHT, result });

export const RightFrom = fn => (...args) => Right(fn(...args));
Right.from = RightFrom;

export const fromNullable = left => value => (value == null ? Left(left) : Right(value));

export const of = Right;

export const isLeft = either => either?.tag === LEFT;

export const isRight = either => either?.tag === RIGHT;

export const extLeft = either => (isLeft(either) ? either.error : undefined);
Left.extract = extLeft;

export const extRight = either => (isRight(either) ? either.result : undefined);
Right.extract = extRight;

export const chain = chainFn => either => (isRight(either) ? chainFn(extRight(either)) : either);

export const chainLeft = chainFn => either => (isLeft(either) ? chainFn(extLeft(either)) : either);

export const biChain = (leftChainFn, rightChainFn) => either =>
	// prettier-ignore
	isRight(either) ? rightChainFn(extRight(either)) :
	isLeft(either)  ? leftChainFn(extLeft(either)) :
	/* otherwise */   throwTypeError(`${toStr(either)} is not an either Left or Right`);

export const extRightStrict = either =>
	isRight(either) ? either.result : throwTypeError(`${toStr(either)} is not Right`);

export const extLeftStrict = either =>
	isLeft(either) ? either.error : throwTypeError(`${toStr(either)} is not Left`);

export const strict = {
	extLeft: extLeftStrict,
	extRight: extRightStrict,
};

export const match = (onLeft, onRight) => either =>
	// prettier-ignore
	isLeft(either)  ? onLeft(extLeft(either)) :
	isRight(either) ? onRight(extRight(either)) :
	/* otherwise */   throwTypeError(`${toStr(either)} is not an either Left or Right`);

export const tap = effect => either => {
	if (isRight(either)) effect(extRight(either));
	return either;
};

export const tapLeft = effect => either => {
	if (isLeft(either)) effect(extLeft(either));
	return either;
};

export const biTap = (leftEffect, rightEffect) => either => {
	if (isRight(either)) rightEffect(extRight(either));
	if (isLeft(either)) leftEffect(extLeft(either));
	return either;
};

export const map = mapper => either =>
	// prettier-ignore
	isRight(either)
		? Right(mapper(extRight(either))) 
		: either;

export const mapLeft = mapper => either =>
	// prettier-ignore
	isLeft(either) 
		? Left(mapper(extLeft(either)))
		: either;

export const biMap = (leftMapper, rightMapper) => either =>
	// prettier-ignore
	isRight(either) ? Right(rightMapper(extRight(either))) :
	isLeft(either)  ? Left(leftMapper(extLeft(either))) :
	/* otherwise */   throwTypeError(`${toStr(either)} is not an either Left or Right`);

export const apply = argEither =>
	chain(fn =>
		isLeft(argEither)
			? argEither //
			: Right(fn(extRight(argEither))),
	);

export const validate = validator =>
	chain(value => {
		const validationResult = validator(value);
		return isLeft(validationResult) ? validationResult : Right(value);
	});
